import { useEffect, useRef, useState, type RefObject } from "react";
import { X } from "lucide-react";
import { useTranslation } from "../../i18n";
import { Button, buttonVariants } from "../ui/button";
import { Dialog } from "../ui/dialog";
import { MotionHeading } from "../ui/motion-heading";
import type { ResolvedShowcaseMedia } from "../../lib/showcase-media";
import { useShowcaseMedia } from "../showcase-media-provider";

type GalleryImage = ResolvedShowcaseMedia["imageColumns"][number][number];

// Each gallery owns its visibility and motion preference; neither controls the existing showcase.
function useGalleryActivity(root: RefObject<HTMLElement | null>) {
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    let intersecting = false;
    const updateVisibility = () => setVisible(intersecting && !document.hidden);
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReducedMotion(preference.matches);
    const observer = new IntersectionObserver(([entry]) => {
      intersecting = entry.isIntersecting;
      updateVisibility();
    }, { threshold: 0.08 });
    observer.observe(element);
    document.addEventListener("visibilitychange", updateVisibility);
    preference.addEventListener("change", updateMotion);
    updateMotion();
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", updateVisibility);
      preference.removeEventListener("change", updateMotion);
    };
  }, [root]);
  return { visible, reducedMotion };
}

function AccordionVideo({ media, playing }: { media: ResolvedShowcaseMedia["accordionVideos"][number]; playing: boolean }) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) void video.play().catch(() => { /* Keep the poster when browser autoplay is unavailable. */ });
    else video.pause();
    return () => video.pause();
  }, [playing]);
  return <>
    <video ref={videoRef} data-gallery-video className="home-accordion-video" id={`home-clip-${media.id}`}
      src={media.src} poster={media.poster} preload="none"
      muted loop playsInline disablePictureInPicture disableRemotePlayback aria-label={t(media.titleKey)}
      onError={() => setFailed(true)} />
    {failed ? <span role="status" className={`home-accordion-error
  absolute [z-index:2] [inset:60px_16px_auto] p-2 [background:var(--background)] text-muted-foreground [font-size:12px] pointer-events-none
`}>{t("creativeHome.mediaError")}</span> : null}
  </>;
}

export function HomeVideoAccordion() {
  const videos = useShowcaseMedia().accordionVideos;
  const { t } = useTranslation();
  const root = useRef<HTMLElement>(null);
  const triggers = useRef<Array<HTMLButtonElement | null>>([]);
  const [selected, setSelected] = useState(0);
  const { visible, reducedMotion } = useGalleryActivity(root);
  return <section ref={root} className={`home-section home-gallery-section
  pt-25 [scroll-margin-top:calc(var(--site-navigation-height,79px)_+_24px)]
  maxw-[760px]:pt-18
`} id="home-video-accordion" aria-label={t("creativeHome.galleries.videoTitle")}>
    <div className={`home-gallery-heading home-video-heading
  flex justify-between items-end gap-6 mb-8
  maxw-[700px]:items-start maxw-[700px]:flex-col maxw-[700px]:gap-5
  [&.home-video-heading]:block [container-type:inline-size] text-center
`}>
      <div>
        <MotionHeading text={t("creativeHome.galleries.videoTitle")} />
        <p className="home-gallery-description">{t("creativeHome.galleries.videoDescription")}</p></div>
    </div>
    <div className={`home-accordion
  flex gap-3 [height:clamp(360px,34vw,540px)]
  maxw-[700px]:flex-col maxw-[700px]:h-auto maxw-[700px]:gap-2.5
`}>
      {videos.map((media, index) => <article key={media.id} className={`home-accordion-panel
  relative isolate flex-1 min-w-0 overflow-hidden [border-radius:24px] [background:var(--muted)] [transition:flex_.65s_cubic-bezier(.22,1,.36,1)]
  [&[data-active=true]]:[flex:7]
  [&[data-active=true]_.home-accordion-video]:[filter:none]
  [&[data-active=false]_.home-accordion-caption]:[writing-mode:vertical-rl] [&[data-active=false]_.home-accordion-caption]:whitespace-nowrap
  [&[data-active=false]_.home-accordion-caption>span]:hidden
  maxw-[700px]:flex-none maxw-[700px]:h-16 maxw-[700px]:[border-radius:16px] maxw-[700px]:[transition:height_.5s_cubic-bezier(.22,1,.36,1)]
  maxw-[700px]:[&[data-active=true]]:flex-none maxw-[700px]:[&[data-active=true]]:[height:clamp(240px,56vw,360px)]
  maxw-[700px]:[&[data-active=false]_.home-accordion-caption]:[writing-mode:horizontal-tb] maxw-[700px]:[&[data-active=false]_.home-accordion-caption]:pl-7
  maxw-[700px]:[&[data-active=true]_.home-accordion-trigger]:[background:linear-gradient(transparent_45%,#000a)]
  motion-reduce:[transition:none]
`} data-video-surface data-active={selected === index}>
        <AccordionVideo key={media.src} media={media} playing={visible && !reducedMotion && selected === index} />
        <button type="button" ref={element => { triggers.current[index] = element; }} className={`home-accordion-trigger
  absolute inset-0 flex items-end w-full border-0 [padding:28px_24px] text-left [color:#fff] [background:linear-gradient(transparent_55%,#0009)] cursor-pointer
  [&:focus-visible]:[outline:3px_solid_#fff] [&:focus-visible]:[outline-offset:-6px]
  maxw-[960px]:[padding:24px_16px]
  maxw-[700px]:p-4
`}
          aria-expanded={selected === index} aria-controls={`home-clip-${media.id}`}
          onPointerEnter={event => { if (event.pointerType === "mouse") setSelected(index); }}
          onClick={() => setSelected(index)} onFocus={() => setSelected(index)}
          onKeyDown={event => {
            const direction = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
            if (!direction && event.key !== "Home" && event.key !== "End") return;
            event.preventDefault();
            const next = event.key === "Home" ? 0 : event.key === "End" ? videos.length - 1 : (index + direction + videos.length) % videos.length;
            triggers.current[next]?.focus();
          }}>
          <span className={`home-accordion-number
  absolute top-6 left-6 [font-size:14px] [line-height:1]
  maxw-[960px]:left-4
  maxw-[700px]:top-6
`} aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
          <span className={`home-accordion-caption
  flex flex-col gap-2 min-w-0
  [&_strong]:[font-size:clamp(18px,2vw,28px)] [&_strong]:font-medium [&_strong]:[line-height:1.3]
  [&>span]:[font-size:14px] [&>span]:[line-height:1.6] [&>span]:[max-width:34em]
  maxw-[700px]:[&_strong]:[font-size:20px]
  maxw-[700px]:[&>span]:[font-size:12px]
`}><strong>{t(media.titleKey)}</strong>
            <span>{t(media.descriptionKey)}</span></span>
        </button>
      </article>)}
    </div>
  </section>;
}

export function HomeDriftWall({ background = false, active = true }: Readonly<{ background?: boolean; active?: boolean }>) {
  const imageColumns = useShowcaseMedia().imageColumns;
  const { t } = useTranslation();
  const root = useRef<HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<GalleryImage["id"] | null>(null);
  const selected = imageColumns.flat().find(image => image.id === selectedId) ?? null;
  const { visible, reducedMotion } = useGalleryActivity(root);
  const title = (image: GalleryImage) => t(image.titleKey);
  return <section ref={root} className={background ? "home-drift-backdrop" : `home-section home-gallery-section
  pt-25 [scroll-margin-top:calc(var(--site-navigation-height,79px)_+_24px)]
  maxw-[760px]:pt-18
`} id={background ? undefined : "home-drift-wall"} aria-hidden={background || undefined} aria-label={background ? undefined : t("creativeHome.galleries.imageTitle")}>
    {!background ? <div className={`home-gallery-heading home-drift-heading
  flex justify-between items-end gap-6 mb-8
  maxw-[700px]:items-start maxw-[700px]:flex-col maxw-[700px]:gap-5
  [&.home-drift-heading]:block [container-type:inline-size] text-center
`}>
      <div>
        <MotionHeading text={t("creativeHome.galleries.imageTitle")} />
        <p className="home-gallery-description">{t("creativeHome.galleries.imageDescription")}</p></div>
    </div> : null}
    <div className={`home-drift-wall
  relative isolate [height:720px] overflow-hidden [border-radius:24px] [background:var(--muted)] [perspective:1200px]
  [&[data-paused=true]_.home-drift-track]:[animation-play-state:paused]
  [&[data-reduced-motion=true]]:overflow-auto
  [&:has(button:focus-visible)]:overflow-auto
  [&[data-reduced-motion=true]_.home-drift-canvas]:min-w-0 [&[data-reduced-motion=true]_.home-drift-canvas]:w-auto [&[data-reduced-motion=true]_.home-drift-canvas]:m-0 [&[data-reduced-motion=true]_.home-drift-canvas]:p-5 [&[data-reduced-motion=true]_.home-drift-canvas]:[transform:none]
  [&:has(button:focus-visible)_.home-drift-canvas]:min-w-0 [&:has(button:focus-visible)_.home-drift-canvas]:w-auto [&:has(button:focus-visible)_.home-drift-canvas]:m-0 [&:has(button:focus-visible)_.home-drift-canvas]:p-5 [&:has(button:focus-visible)_.home-drift-canvas]:[transform:none]
  [&[data-reduced-motion=true]_.home-drift-track]:animate-none [&[data-reduced-motion=true]_.home-drift-track]:[will-change:auto]
  [&:has(button:focus-visible)_.home-drift-track]:animate-none [&:has(button:focus-visible)_.home-drift-track]:[will-change:auto]
  [&[data-reduced-motion=true]_[data-copy="1"]]:hidden
  [&:has(button:focus-visible)_[data-copy="1"]]:hidden
  maxw-[960px]:[height:620px]
  maxw-[700px]:[height:560px] maxw-[700px]:[border-radius:16px]
  maxw-[700px]:[&[data-reduced-motion=true]_.home-drift-canvas]:[grid-template-columns:repeat(2,minmax(0,1fr))] maxw-[700px]:[&[data-reduced-motion=true]_.home-drift-canvas]:p-3 maxw-[700px]:[&[data-reduced-motion=true]_.home-drift-canvas]:gap-3
  maxw-[700px]:[&:has(button:focus-visible)_.home-drift-canvas]:[grid-template-columns:repeat(2,minmax(0,1fr))] maxw-[700px]:[&:has(button:focus-visible)_.home-drift-canvas]:p-3 maxw-[700px]:[&:has(button:focus-visible)_.home-drift-canvas]:gap-3
`} data-paused={!active || !visible || Boolean(selected)} data-reduced-motion={reducedMotion}>
      <div className={`home-drift-canvas
  grid [grid-template-columns:repeat(5,minmax(0,1fr))] gap-5 [width:122%] [min-width:960px] [margin-left:-22%] [transform:rotateX(7deg)_rotateY(-9deg)_rotateZ(7deg)] [transform-origin:center_30%]
  maxw-[700px]:[margin-left:calc((100%_-_960px)/2_-_32px)] maxw-[700px]:gap-3.5
`}>
        {imageColumns.map(column => <div className={`home-drift-column
  min-w-0
  [&:nth-child(even)_.home-drift-track]:[animation-direction:reverse] [&:nth-child(even)_.home-drift-track]:[animation-duration:calc(105s_/_1.15)]
  [&:nth-child(3)_.home-drift-track]:[animation-duration:calc(115s_/_1.15)]
  [&:nth-child(4)_.home-drift-track]:[animation-delay:-35s]
  [&:nth-child(5)_.home-drift-track]:[animation-delay:-18s]
`} key={column[0].id}>
          <div className={`home-drift-track
  animate-[home-drift-rise_calc(90s_/_1.15)_linear_infinite] [will-change:transform]
  motion-reduce:animate-none
`}>
            {[0, 1].map(copy => <div className={`home-drift-stack
  flex flex-col gap-5 pb-5
  maxw-[700px]:gap-3.5 maxw-[700px]:pb-3.5
`} key={copy} aria-hidden={copy === 1 ? true : undefined} data-copy={copy}>
              {column.map(image => background ? <div className={`home-drift-image
  relative block w-full shrink-0 overflow-hidden border-0 [border-radius:12px] p-0 [background:var(--background)] text-left cursor-pointer
  [&_img]:block [&_img]:w-full [&_img]:h-auto [&_img]:[min-height:130px] [&_img]:object-cover
  [&>span]:absolute [&>span]:[inset:auto_0_0] [&>span]:[padding:28px_16px_16px] [&>span]:[background:linear-gradient(transparent,#000b)] [&>span]:[color:#fff] [&>span]:[font-size:14px] [&>span]:[opacity:0] [&>span]:[transition:opacity_.2s]
  [&:is(:hover,:focus-visible)>span]:[opacity:1]
  [&:focus-visible]:[outline:3px_solid_var(--ring)] [&:focus-visible]:[outline-offset:-3px]
  motion-reduce:[&>span]:[transition:none]
`} key={image.id}>
                <img src={image.src} alt="" width={image.width} height={image.height} loading={visible ? "eager" : "lazy"} decoding="async" />
              </div> : <button type="button" className={`home-drift-image
  relative block w-full shrink-0 overflow-hidden border-0 [border-radius:12px] p-0 [background:var(--background)] text-left cursor-pointer
  [&_img]:block [&_img]:w-full [&_img]:h-auto [&_img]:[min-height:130px] [&_img]:object-cover
  [&>span]:absolute [&>span]:[inset:auto_0_0] [&>span]:[padding:28px_16px_16px] [&>span]:[background:linear-gradient(transparent,#000b)] [&>span]:[color:#fff] [&>span]:[font-size:14px] [&>span]:[opacity:0] [&>span]:[transition:opacity_.2s]
  [&:is(:hover,:focus-visible)>span]:[opacity:1]
  [&:focus-visible]:[outline:3px_solid_var(--ring)] [&:focus-visible]:[outline-offset:-3px]
  motion-reduce:[&>span]:[transition:none]
`} key={image.id} tabIndex={copy === 1 ? -1 : 0}
                onClick={() => setSelectedId(image.id)} aria-label={t("creativeHome.viewImage", { title: title(image) })}>
                <img src={image.src} alt={copy === 0 ? title(image) : ""} width={image.width} height={image.height} loading={visible ? "eager" : "lazy"} decoding="async" />
                <span>{title(image)}</span>
              </button>)}
            </div>)}
          </div>
        </div>)}
      </div>
    </div>
    {selected ? <Dialog title={title(selected)} pending={false} onClose={() => setSelectedId(null)} className="home-image-dialog !max-w-6xl">
      <div className="mb-4 flex items-center justify-between gap-4"><h3>{title(selected)}</h3>
        <Button variant="ghost" size="icon" onClick={() => setSelectedId(null)} aria-label={t("workspace.close")}><X /></Button></div>
      <img className="mx-auto block max-h-[70dvh] max-w-full object-contain" src={selected.src} alt={title(selected)} />
      <a className={buttonVariants({ variant: "link", className: "mt-3" })} href={selected.src} target="_blank" rel="noopener noreferrer">{t("creativeHome.viewImage", { title: title(selected) })}</a>
    </Dialog> : null}
  </section>;
}
